import React, { useState, useEffect, useCallback } from 'react';
import Modal from './Modal';
import { QRCodeSVG } from 'qrcode.react';
import {
  sharingApi,
  InviteCreated,
  GranteeItem,
  PendingInviteRow,
} from '../api/sharing';
import { ensureLinkEnvelopesForMemberInvite } from '../utils/e2eeClient';
import { formatApiError } from '../utils/errorUtils';
import { useIsAdvanced } from '../utils/viewMode';
import './ShareInviteModal.css';

type ShareKind = 'family' | 'member';

interface ShareInviteModalProps {
  open: boolean;
  onClose: () => void;
  kind: ShareKind;
  familyId?: number;
  memberId?: number;
}

const ShareInviteModal: React.FC<ShareInviteModalProps> = ({ open, onClose, kind, familyId, memberId }) => {
  const isAdvanced = useIsAdvanced();
  const [invite, setInvite] = useState<InviteCreated | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const [grantees, setGrantees] = useState<GranteeItem[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInviteRow[]>([]);
  const [mgmtLoading, setMgmtLoading] = useState(false);
  const [busyUserId, setBusyUserId] = useState<number | null>(null);
  const [busyInviteId, setBusyInviteId] = useState<number | null>(null);
  const [linkMsg, setLinkMsg] = useState<string>('');

  const loadManagement = useCallback(async () => {
    if (!open) return;
    if (kind === 'family' && familyId == null) return;
    if (kind === 'member' && memberId == null) return;
    setMgmtLoading(true);
    try {
      if (kind === 'family' && familyId != null) {
        const [g, p] = await Promise.all([
          sharingApi.listFamilyGrantees(familyId),
          sharingApi.listFamilyPendingInvites(familyId),
        ]);
        setGrantees(g);
        setPendingInvites(p);
      } else if (kind === 'member' && memberId != null) {
        const [g, p] = await Promise.all([
          sharingApi.listMemberGrantees(memberId),
          sharingApi.listMemberPendingInvites(memberId),
        ]);
        setGrantees(g);
        setPendingInvites(p);
      }
    } catch {
      setGrantees([]);
      setPendingInvites([]);
    } finally {
      setMgmtLoading(false);
    }
  }, [open, kind, familyId, memberId]);

  useEffect(() => {
    if (!open) {
      setInvite(null);
      setError('');
      setCopied(false);
      setGrantees([]);
      setPendingInvites([]);
      return;
    }
    loadManagement();
  }, [open, loadManagement]);

  const fullUrl =
    invite &&
    (invite.url.startsWith('http')
      ? invite.url
      : `${window.location.origin.replace(/\/$/, '')}${invite.url.startsWith('/') ? '' : '/'}${invite.url}`);

  const handleGenerate = async () => {
    setError('');
    setLinkMsg('');
    setInvite(null);
    setLoading(true);
    try {
      if (kind === 'family' && familyId != null) {
        const inv = await sharingApi.createFamilyInvite(familyId);
        setInvite(inv);
        // Best-effort: create secret-link envelopes for E2EE docs in this family by iterating members.
        const { familiesApi } = await import('../api/families');
        const members = await familiesApi.getMembers(familyId);
        let processed = 0;
        let skipped = 0;
        const errors: Array<{ document_id: number; error: string }> = [];
        for (const m of members) {
          const r = await ensureLinkEnvelopesForMemberInvite(m.id, inv.token);
          processed += r.processed;
          skipped += r.skipped;
          if (r.errors?.length) errors.push(...r.errors);
        }
        if (processed || skipped) {
          const base = `Secret link setup: processed=${processed}, skipped=${skipped}`;
          const top = errors
            .slice(0, 2)
            .map((x) => `#${x.document_id}: ${x.error}`)
            .join(' | ');
          setLinkMsg(errors.length ? `${base}. Errors: ${top}${errors.length > 2 ? ' | …' : ''}` : base);
        }
      } else if (kind === 'member' && memberId != null) {
        const inv = await sharingApi.createMemberInvite(memberId);
        setInvite(inv);
        const r = await ensureLinkEnvelopesForMemberInvite(memberId, inv.token);
        if (r.processed || r.skipped) {
          const base = `Secret link setup: processed=${r.processed}, skipped=${r.skipped}`;
          const top = (r.errors || [])
            .slice(0, 2)
            .map((x) => `#${x.document_id}: ${x.error}`)
            .join(' | ');
          setLinkMsg((r.errors || []).length ? `${base}. Errors: ${top}${(r.errors || []).length > 2 ? ' | …' : ''}` : base);
        }
      } else {
        setError('Date lipsă pentru invitație.');
      }
      await loadManagement();
    } catch (e: any) {
      setError(formatApiError(e.response?.data?.detail) || 'Nu s-a putut crea invitația.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveGrantee = async (userId: number) => {
    if (kind === 'family' && familyId == null) return;
    if (kind === 'member' && memberId == null) return;
    setBusyUserId(userId);
    setError('');
    try {
      if (kind === 'family' && familyId != null) {
        await sharingApi.removeFamilyGrantee(familyId, userId);
      } else if (kind === 'member' && memberId != null) {
        await sharingApi.removeMemberGrantee(memberId, userId);
      }
      await loadManagement();
    } catch (e: any) {
      setError(formatApiError(e.response?.data?.detail) || 'Nu s-a putut elimina accesul.');
    } finally {
      setBusyUserId(null);
    }
  };

  const handleRevokeInvite = async (inviteId: number) => {
    setBusyInviteId(inviteId);
    setError('');
    try {
      await sharingApi.revokeInviteRow(inviteId);
      await loadManagement();
      setInvite(null);
    } catch (e: any) {
      setError(formatApiError(e.response?.data?.detail) || 'Nu s-a putut revoca invitația.');
    } finally {
      setBusyInviteId(null);
    }
  };

  const handleCopy = async () => {
    if (!fullUrl) return;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Copierea în clipboard a eșuat.');
    }
  };

  const handleClose = () => {
    setInvite(null);
    setError('');
    setCopied(false);
    onClose();
  };

  const title = kind === 'family' ? 'Partajare familie' : 'Partajare membru';

  const granteeLabel = (g: GranteeItem) =>
    (g.full_name && g.full_name.trim()) || g.email || `Utilizator #${g.user_id}`;

  const showEmailSubline = (g: GranteeItem) => {
    const primary = (g.full_name && g.full_name.trim()) || g.email || '';
    return Boolean(g.email && primary !== g.email);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={handleClose}>
            Închide
          </button>
          {!invite ? (
            <button type="button" className="btn btn-primary" onClick={handleGenerate} disabled={loading}>
              {loading ? 'Se generează…' : isAdvanced ? 'Generează link (7 zile)' : 'Trimite invitație'}
            </button>
          ) : (
            <button type="button" className="btn btn-secondary" onClick={handleGenerate} disabled={loading}>
              {loading ? 'Se generează…' : isAdvanced ? 'Generează link nou' : 'Creează altă invitație'}
            </button>
          )}
        </>
      }
    >
      <div className="share-invite-modal">
        <section className="share-invite-section" aria-labelledby="share-grantees-title">
          <div className="share-invite-section__head">
            <h3 id="share-grantees-title" className="share-invite-section__title">
              Persoane cu acces
            </h3>
          </div>
          <div className="share-invite-section__body">
            {mgmtLoading ? (
              <p className="share-invite-empty">Se încarcă…</p>
            ) : grantees.length === 0 ? (
              <p className="share-invite-empty">
                Nimeni nu are încă acces prin partajare. Folosește butonul de jos pentru a crea un link de invitație.
              </p>
            ) : (
              <ul className="share-invite-list">
                {grantees.map((g) => (
                  <li key={g.user_id} className="share-invite-row">
                    <div className="share-invite-row__main">
                      <div className="share-invite-row__name">{granteeLabel(g)}</div>
                      {showEmailSubline(g) && (
                        <div className="share-invite-row__meta">{g.email}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      disabled={busyUserId === g.user_id}
                      onClick={() => handleRemoveGrantee(g.user_id)}
                    >
                      {busyUserId === g.user_id ? '…' : 'Elimină accesul'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* `linkMsg` is the raw "processed=…, skipped=…, Errors: #42: <stack>" debug
            string from the envelope-setup step. Useful when triaging a sharing bug,
            but pure noise for non-tech users. */}
        {isAdvanced && linkMsg.trim() && (
          <div className="alert" role="status" style={{ marginTop: 'var(--sp-3)' }}>
            {linkMsg}
          </div>
        )}

        <section className="share-invite-section" aria-labelledby="share-pending-title">
          <div className="share-invite-section__head">
            <h3 id="share-pending-title" className="share-invite-section__title">
              Invitații în așteptare
            </h3>
          </div>
          <div className="share-invite-section__body">
            {mgmtLoading ? (
              <p className="share-invite-empty">Se încarcă…</p>
            ) : pendingInvites.length === 0 ? (
              <p className="share-invite-empty">Nicio invitație activă momentan.</p>
            ) : (
              <ul className="share-invite-list">
                {pendingInvites.map((inv) => (
                  <li key={inv.id} className="share-invite-row">
                    <div className="share-invite-row__main">
                      <div className="share-invite-row__name">
                        {isAdvanced
                          ? `Link …${inv.token_hint || '****'}`
                          : `Invitație #${inv.id}`}
                      </div>
                      {inv.expires_at && (
                        <div className="share-invite-row__meta">
                          exp. {new Date(inv.expires_at).toLocaleString('ro-RO')}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={busyInviteId === inv.id}
                      onClick={() => handleRevokeInvite(inv.id)}
                    >
                      {busyInviteId === inv.id ? '…' : 'Revocă'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="share-invite-link-section" aria-labelledby="share-link-title">
          <div className="share-invite-link-section__head">
            <h3 id="share-link-title" className="share-invite-link-section__title">
              Link de invitație
            </h3>
            <p className="share-invite-link-section__lead">
              Linkul și codul QR pot fi trimise unei persoane. După autentificare, poate accepta invitația. Valabilitate:
              7 zile de la generare.
            </p>
          </div>
          <div className="share-invite-link-section__body">
            {error && (
              <div className="alert alert-error share-invite-alert" role="alert">
                {error}
              </div>
            )}
            {invite ? (
              <>
                {/* The raw token URL is long, technical, and easy to accidentally
                    truncate when copying by hand. In simplified mode we hide it
                    and rely on the Copy + QR affordances below. */}
                {isAdvanced && (
                  <>
                    <div className="share-invite-url-label">Link</div>
                    <div className="share-invite-url-box">{fullUrl}</div>
                  </>
                )}
                <div className="share-invite-url-actions">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={handleCopy}>
                    {copied ? 'Copiat!' : 'Copiază linkul'}
                  </button>
                  <span className="share-invite-expires">
                    Expiră: {new Date(invite.expires_at).toLocaleString('ro-RO')}
                  </span>
                </div>
                {fullUrl && (
                  <div className="share-invite-qr">
                    <div className="share-invite-qr__label">Cod QR</div>
                    <div className="share-invite-qr__frame">
                      <QRCodeSVG value={fullUrl} size={180} level="M" />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="share-invite-empty share-invite-empty--flush">
                După generare, vei vedea aici linkul și codul QR. Poți copia linkul sau reveni oricând din acest
                dialog.
              </p>
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
};

export default ShareInviteModal;
